import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { Paged } from 'api/common/types/Page';
import styles from './MaintenanceComponentsList.module.scss';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import { FaEllipsisH } from 'react-icons/fa';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import { MaintenanceComponentDto } from 'api/maintenanceComponents/models/MaintenanceComponentDto';
import { MaintenanceComponentsSearchCriteria } from 'api/maintenanceComponents/models/MaintenanceComponentsSearchCriteria';
import MaintenanceComponentsService from 'api/maintenanceComponents/MaintenanceComponentsService';
import { useHistory } from 'react-router-dom';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import AddIcon from 'assets/svg/desktop_add_new.svg';

type Props = {
};

const MaintenanceComponentsList: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const isFirstLoad = useRef(true);
    const filterJsonKey = 'PARAMETERS_MAINTENANCE_COMPONENT_FILTER';

    const [maintenanceComponentsPage, setMaintenanceComponentsPage] = useState<Paged<MaintenanceComponentDto>>();
    const [criteria, setCriteria] = useState<MaintenanceComponentsSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'name',
        orderBy: 'asc'
    });

    const history = useHistory();

    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<MaintenanceComponentDto | null>(null);

    const showRemoveItemDialog = (item: MaintenanceComponentDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    };

    const renderTableActionCell = (row: MaintenanceComponentDto) => {
        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={`/settings/maintenanceComponents/details/${row.id ?? ''}`}>
                            {t('common.details')}
                        </DropdownItem>
                        <DropdownItem url={`/settings/maintenanceComponents/edit/${row.id ?? ''}`}>
                            {t('common.edit')}
                        </DropdownItem>
                        <DropdownItem onClick={() => showRemoveItemDialog(row)}>
                            {t('common.remove')}
                        </DropdownItem>
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<MaintenanceComponentDto>[] = useMemo(() => {
        const cols: ListingTableColumn<MaintenanceComponentDto>[] = [
            {
                field: 'name',
                name: t('maintenance_components.list.name'),
                onSearch: onTableFilter,
                searchField: 'mc.name'
            },
            {
                name: '',
                width: '60px',
                cellAlignment: 'right',
                preventClick: true,
                renderCell: renderTableActionCell,
                cellStyle: { overflow: 'unset' },
            },
        ];
        return cols;
    }, []);

    const getData = async () => {
        try {
            Loading.show();

            saveOrdernationFilters();

            const page = await MaintenanceComponentsService.getList(criteria);
            setMaintenanceComponentsPage(page);

            Loading.hide();
        } catch (error) {
            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't get maintenance components list`,
                error
            );
            addToast(t('common.messages.error_load_info'), {
                appearance: 'error',
            });
            Loading.hide();
        }
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            await MaintenanceComponentsService.remove(itemToRemove);
            setCriteria({ ...criteria, page: 1 });
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete maintenance component`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setCriteria({ ...criteria, page: 1, allIn: value });
    }, 500);

    useEffect(() => {
        if (isFirstLoad.current == true) {
            loadFilters()
        } else {
            getData()
        }
    }, [criteria]);

    const saveOrdernationFilters = () => {
        saveFiltersOnCache(criteria);
    }

    const saveFiltersOnCache = (f: MaintenanceComponentsSearchCriteria) => {
        localStorage.setItem(filterJsonKey, JSON.stringify(f));
    }

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(filterJsonKey);
        if (cachedFilters != null) { onChangeFilters(JSON.parse(cachedFilters), true) }
        else { getData() }

        isFirstLoad.current = false;
    }

    const onChangeFilters = (f: MaintenanceComponentsSearchCriteria, isOnLoad?: boolean) => {
        saveFiltersOnCache(f);
        setCriteria(prev => ({
            ...prev,
            ...f,
            page: (isOnLoad && f.page) ? f.page : 1,
        }))
    }

    return (
        <div className='list'>
            <div className={styles.tabHeader}>
                <div>
                    <InputSearch
                        onChangeValue={debounced}
                        placeholder={t('maintenance_components.search_placeholder')}
                        defaultValue={criteria.allIn}
                    ></InputSearch>
                </div>
                <div>
                    <ScreenHeaderButton icon={AddIcon} onClick={() => history.push(`/settings/maintenanceComponents/new`)} />
                </div>
            </div>
            {!isFirstLoad.current && <ListingTable
                columns={tableColumns}
                rows={maintenanceComponentsPage?.items || []}
                onRowClick={row => history.push(`/settings/maintenanceComponents/details/${row.id ?? ''}`)}
                onHref={row => `/settings/maintenanceComponents/details/${row.id ?? ''}`}
                allowHover={true}
                initialSearch={{ colField: criteria.orderColumn ?? 'mc.name', isOrderAsc: (criteria.orderBy && criteria.orderBy == 'asc') ? true : false }}
            />}
            <PaginationWithInfo
                itemName={t('maintenance_components.title')}
                currentPage={maintenanceComponentsPage?.currentPage || 1}
                pageItems={maintenanceComponentsPage?.items.length || 0}
                totalItems={maintenanceComponentsPage?.totalItems || 0}
                onChange={page => setCriteria({ ...criteria, page })}
            />
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_maintenance_component', { name: itemToRemove?.name ?? '' })} />

        </div>
    );
};

export default MaintenanceComponentsList;
